import { Select, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useDebouncedValue from "hooks/useDebouncedValue";
import { useInfiniteApiQuery } from "lib/react-query";
import LoadMorePopup from "./LoadMorePopup";

/**
 * LoadMoreSelect — async Ant Design Select that pages through a paginated
 * backend endpoint, appending more options as the user scrolls to the bottom
 * of the dropdown ("load more"). Use for dropdowns backed by the dashboard
 * `*-search` / lookup endpoints (which now return paginated results).
 *
 * The debounced search text is sent as `search` (override with `searchParamKey`).
 * The initial `editedValue` is sent once as `id` so edit/show flows can resolve
 * the pre-selected label on page 1 — selecting a new option does not re-query
 * by id; clearing drops `id` and search so options reset to the first page.
 *
 * @param {string} url - API path, e.g. "dashboard/customers-search"
 * @param {any} value - Selected value (id) — controlled
 * @param {(value:any)=>void} onChange - Selection handler
 * @param {(item:object)=>{label:string,value:any}} mapOption - Maps a raw record to an option
 * @param {object|null} extraParams - Extra query params sent on every request
 * @param {string} searchParamKey - Query key for the search term (default "search")
 * @param {number} perPage - Page size (default 15)
 * @param {number} debounceMs - Search debounce (default 400)
 * @param {any} editedValue - Pre-selected id for edit/show flows (falls back to `value` when inside Form.Item)
 * @param {Array} seededOptions - Options to keep visible even if not in the fetched page
 * @param {Array} excludeIds - Option values to drop from the fetched/seeded options (e.g. the record's own id)
 * @param {string} mode - Ant Select mode (e.g. "multiple")
 * @param {boolean} enabled - Gate the query (default true)
 * @param {object} queryOptions - Extra React Query options (e.g. `{ gcTime: 0 }` to keep
 *   no cache once the select unmounts)
 */
const isClearedValue = (nextValue) =>
  nextValue == null ||
  nextValue === "" ||
  (Array.isArray(nextValue) && nextValue.length === 0);

const LoadMoreSelect = ({
  editedValue,
  url,
  value,
  onChange,
  mapOption = (item) => ({
    label:
      item?.full_name ||
      item?.name ||
      item?.title ||
      [item?.fname, item?.lname].filter(Boolean).join(" ").trim() ||
      item?.serial ||
      item?.serial_number ||
      `#${item?.id}`,
    value: item?.id,
  }),
  extraParams = null,
  searchParamKey = "search",
  perPage = 15,
  debounceMs = 400,
  seededOptions = [],
  excludeIds = [],
  placeholder,
  disabled = false,
  allowClear = true,
  mode,
  enabled = true,
  queryOptions,
  scrollThreshold = 40,
  className = "",
  ...selectProps
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  // Only the pre-interaction edit id is pinned for `id=` — never the live selection.
  const userInteractedRef = useRef(false);
  const [pinnedId, setPinnedId] = useState(() =>
    editedValue != null && !Array.isArray(editedValue) ? editedValue : null
  );
  const debouncedSearch = useDebouncedValue(
    typeof search === "string" ? search.trim() : "",
    debounceMs
  );

  useEffect(() => {
    if (
      userInteractedRef.current ||
      pinnedId != null ||
      editedValue == null ||
      Array.isArray(editedValue)
    ) {
      return;
    }
    // Async edit/show payload arrived before the user touched the control.
    setPinnedId(editedValue);
  }, [editedValue, pinnedId]);

  const searchParams = useMemo(() => {
    const params = { ...(extraParams || {}) };
    if (debouncedSearch) {
      params[searchParamKey] = debouncedSearch;
    }
    if (pinnedId != null) {
      params.id = pinnedId;
    }
    return params;
  }, [extraParams, debouncedSearch, searchParamKey, pinnedId]);

  const {
    data,
    fetchLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteApiQuery(url, {
    searchParams,
    perPage,
    enabled,
    extraOptions: queryOptions,
  });

  const options = useMemo(() => {
    // Attach the raw record to each option so consumers can read the full item
    // on selection (via Ant Select's onSelect/onChange `option` arg) — needed for
    // dropdowns that auto-fill sibling fields (price, warehouse, tax, …).
    const fromApi = (data || []).map((item) => ({
      ...mapOption(item),
      record: item,
    }));
    if (debouncedSearch) {
      return fromApi;
    }
    const preserved = (seededOptions || []).filter(
      (opt) => !fromApi.some((o) => o.value === opt.value)
    );
    const merged = [...preserved, ...fromApi];
    return excludeIds?.length
      ? merged.filter((opt) => !excludeIds.includes(opt.value))
      : merged;
  }, [data, seededOptions, mapOption, excludeIds]);

  const handleChange = (nextValue, option) => {
    userInteractedRef.current = true;
    setSearch("");
    if (isClearedValue(nextValue)) {
      // Drop pinned id so the list resets to the default first page.
      setPinnedId(null);
    }
    onChange?.(nextValue, option);
  };

  const handlePopupScroll = (event) => {
    const el = event.target;
    const reachedBottom =
      el.scrollTop + el.offsetHeight >= el.scrollHeight - scrollThreshold;
    if (reachedBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // A search with no matching option leaves stale text in the box on blur —
  // AntD only clears it on selection, not on blur. Drive the input as a
  // controlled value so leaving it empty here actually clears what's shown.
  const handleBlur = (event) => {
    setSearch("");
    selectProps.onBlur?.(event);
  };

  return (
    <Select
      mode={mode}
      showSearch
      filterOption={false}
      options={options}
      loading={fetchLoading || isFetchingNextPage}
      placeholder={placeholder ?? t("placeholder.select")}
      disabled={disabled}
      allowClear={allowClear}
      optionFilterProp="label"
      notFoundContent={
        error ? (
          <span style={{ color: "red", padding: "8px" }}>
            {t("errors.loadFailed")}
          </span>
        ) : fetchLoading ? (
          <Spin size="small" />
        ) : (
          t("noData")
        )
      }
      popupRender={(menu) => (
        <LoadMorePopup menu={menu} loadingMore={isFetchingNextPage} />
      )}
      {...selectProps}
      className={className}
      value={value ?? undefined}
      searchValue={search}
      onChange={handleChange}
      onSearch={setSearch}
      onBlur={handleBlur}
      onPopupScroll={handlePopupScroll}
    />
  );
};

export default LoadMoreSelect;
